/* Warehouse Bold — product hero behaviour.
 *
 * Owns: gallery slide/thumb sync, the MOQ-aware quantity stepper, the
 * sticky buy bar mirror + reveal, back-to-top, and the dispatch countdown.
 * Add-to-cart itself stays with the theme's product form.
 */
(() => {
  const init = () => {
    const root = document.querySelector('.wb-product');
    if (!root) return;
    const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------------- Gallery ---------------- */
    const gallery = root.querySelector('[data-wb-gallery]');
    const track = root.querySelector('[data-wb-track]');
    const thumbsBox = root.querySelector('[data-wb-thumbs]');
    if (gallery && track) {
      const thumbs = [...root.querySelectorAll('[data-wb-thumb]')];
      const slides = [...track.querySelectorAll('[data-wb-slide]')];
      const prev = root.querySelector('[data-wb-prev]');
      const next = root.querySelector('[data-wb-next]');
      const moreBtn = root.querySelector('[data-wb-thumb-more]');
      /* ⚠ Start from the slide the SERVER marked active, not from 0. On a
         `?variant=` link — what a colour swatch on a product card produces —
         wb-main-product.liquid puts `is-active` on the selected variant's
         thumb, and the track has to agree or the two disagree on screen: the
         right thumb outlined, the wrong photo beside it.
         Falls back to 0 if nothing is marked, which is also the single-image
         and no-variant case. */
      let active = Math.max(0, thumbs.findIndex((t) => t.classList.contains('is-active')));
      /* Target lock: clicking a distant thumb must not light every thumb it
         passes on the way. Released on arrival, on scrollend, on any user
         input (their scrolling takes over), and a 1.5s safety timeout for
         browsers without scrollend. */
      let lock = null, lockT = null;
      const releaseLock = () => { lock = null; clearTimeout(lockT); sync(); };

      const mark = (i) => {
        active = i;
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
        if (prev) prev.hidden = i === 0;
        if (next) next.hidden = i === slides.length - 1;
        padThumbIntoView(i);
      };
      /* Scroll the STRIP's own scrollTop/Left — deliberately not
         scrollIntoView(), which also scrolls the document and would yank
         the sticky gallery. Targets the zone inside the fades so the tile
         never lands washed out beneath one. */
      const padThumbIntoView = (i) => {
        const t = thumbs[i];
        if (!t || !thumbsBox) return;
        const horizontal = getComputedStyle(thumbsBox).flexDirection.startsWith('row');
        if (horizontal) {
          const left = t.offsetLeft - (thumbsBox.clientWidth - t.offsetWidth) / 2;
          thumbsBox.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'auto' : 'smooth' });
        } else {
          const pad = 58;
          const top = t.offsetTop - pad;
          const bottom = t.offsetTop + t.offsetHeight + pad;
          if (top < thumbsBox.scrollTop) thumbsBox.scrollTo({ top: Math.max(0, top), behavior: reduced() ? 'auto' : 'smooth' });
          else if (bottom > thumbsBox.scrollTop + thumbsBox.clientHeight) thumbsBox.scrollTo({ top: bottom - thumbsBox.clientHeight, behavior: reduced() ? 'auto' : 'smooth' });
        }
      };
      const sync = () => {
        if (lock !== null) return;
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== active && slides[i]) mark(i);
      };
      const goSlide = (i) => {
        if (!slides[i]) return;
        lock = i;
        mark(i);
        track.scrollTo({ left: track.clientWidth * i, behavior: reduced() ? 'auto' : 'smooth' });
        clearTimeout(lockT);
        lockT = setTimeout(releaseLock, 1500);
      };

      /* Put the track where the server says we are, before anything can
         listen. Assigning scrollLeft directly rather than goSlide(): this is
         the opening position, not a transition, so it must not animate and
         must not arm the target lock. `sync()` on the resulting scroll event
         then agrees with `active` instead of dragging it back to 0 — which is
         what defeated every earlier attempt to fix this from wb-variants.js.
         Guarded on clientWidth: a gallery measuring 0 (display:none in a
         closed tab panel) would compute NaN and blank the track. */
      /* ⚠ Takes the LOCK, and that is the whole trick. Assigning scrollLeft
         alone was measured losing the race: the track sat correctly on slide 2
         while the thumb snapped back to 0, because `sync()` runs on the scroll
         events the assignment itself produces and re-derives the active thumb
         from a scroll position that has not settled yet. `lock` is the
         gallery's existing answer to exactly that problem — goSlide() sets it
         so a long glide does not light every thumb it passes — so the opening
         jump uses it too, and releases the moment the track has arrived.

         scrollLeft, not scrollTo(): this is where the page OPENS, not a
         transition, so it must not animate.

         mark() rather than only padThumbIntoView(), because the prev arrow is
         rendered `hidden` server-side for the usual slide-0 opening and has to
         be un-hidden when we open anywhere else (and `next` hidden when the
         variant's photo is the last one). */
      const openAt = (i) => {
        if (!slides[i] || !track.clientWidth) return;
        lock = i;
        mark(i);
        track.scrollLeft = track.clientWidth * i;
        clearTimeout(lockT);
        lockT = setTimeout(releaseLock, 300);
      };

      /* Re-asserted after layout as well as immediately: at init the track can
         still measure 0 (web font not swapped, image box not resolved), and a
         width of 0 makes the arithmetic a no-op. Both re-asserts stand down as
         soon as the shopper touches the gallery themselves — `galleryOwned`
         flips on any real interaction, so a deep link can never yank the view
         back from under someone who has already started swiping. */
      let galleryOwned = false;
      const openSlide = active;
      if (openSlide > 0) {
        openAt(openSlide);
        requestAnimationFrame(() => { if (!galleryOwned) openAt(openSlide); });
        window.addEventListener('load', () => { if (!galleryOwned) openAt(openSlide); });
      }

      thumbs.forEach((t, i) => t.addEventListener('click', () => { galleryOwned = true; goSlide(i); }));
      if (prev) prev.addEventListener('click', () => { galleryOwned = true; goSlide(active - 1); });
      if (next) next.addEventListener('click', () => { galleryOwned = true; goSlide(active + 1); });
      track.addEventListener('scroll', sync, { passive: true });
      if ('onscrollend' in window) track.addEventListener('scrollend', () => { if (lock !== null) releaseLock(); });
      ['wheel', 'touchstart'].forEach((ev) => track.addEventListener(ev, () => { galleryOwned = true; lock = null; clearTimeout(lockT); }, { passive: true }));

      /* Fades show only on the side that actually has more content, so a
         product with few images never implies scrollability it lacks.
         Recomputed on scroll, resize, and image load (decoding changes
         scrollHeight). */
      const edges = () => {
        if (!thumbsBox) return;
        const vertical = !getComputedStyle(thumbsBox).flexDirection.startsWith('row');
        const size = vertical ? thumbsBox.scrollHeight - thumbsBox.clientHeight : 0;
        const pos = vertical ? thumbsBox.scrollTop : 0;
        gallery.classList.toggle('has-above', vertical && pos > 4);
        const more = vertical && size > 4 && pos < size - 4;
        gallery.classList.toggle('has-more', more);
        if (moreBtn) moreBtn.disabled = !more;
      };
      if (thumbsBox) thumbsBox.addEventListener('scroll', edges, { passive: true });
      addEventListener('resize', edges, { passive: true });
      if (moreBtn) moreBtn.addEventListener('click', () => {
        thumbsBox.scrollBy({ top: thumbsBox.clientHeight * 0.7, behavior: reduced() ? 'auto' : 'smooth' });
      });
      root.querySelectorAll('img').forEach((img) => img.addEventListener('load', edges));
      mark(0);
      edges();
    }

    /* ---------------- Quantity stepper (MOQ-aware) ---------------- */
    const stepper = root.querySelector('.wb-stepper');
    const qtyInput = stepper && stepper.querySelector('.wb-qty');
    const moqNote = root.querySelector('[data-wb-moq-note]');
    const min = stepper ? parseInt(stepper.dataset.min, 10) || 1 : 1;
    const step = stepper ? parseInt(stepper.dataset.step, 10) || 1 : 1;
    const cta = root.querySelector('[data-wb-cta]');

    const validQty = (n) => n >= min && (n - min) % step === 0;
    /* Round UP to the next valid multiple on blur — never down; a shopper
       who typed 5 wants at least 5. */
    const roundUp = (n) => {
      if (n < min) return min;
      const over = (n - min) % step;
      return over === 0 ? n : n + (step - over);
    };
    const paintQty = () => {
      if (!qtyInput) return;
      const n = parseInt(qtyInput.value, 10) || 0;
      const bad = !validQty(n);
      stepper.classList.toggle('is-invalid', bad);
      if (moqNote) moqNote.classList.toggle('is-invalid', bad);
      /* Invalid quantity disables ADD TO CART rather than letting the cart
         reject it later. The app embed stays as the backstop. */
      if (cta && !cta.hasAttribute('data-wb-no-submit')) cta.disabled = bad;
      const minusBtn = stepper.querySelector('[name="minus"]');
      if (minusBtn) minusBtn.disabled = n <= min;
      syncBar();
      paintLineTotal();
    };
    if (stepper && qtyInput) {
      stepper.addEventListener('click', (e) => {
        const b = e.target.closest('button[name]');
        if (!b) return;
        const n = parseInt(qtyInput.value, 10) || min;
        qtyInput.value = b.name === 'plus' ? n + step : Math.max(min, n - step);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        paintQty();
      });
      qtyInput.addEventListener('input', paintQty);
      qtyInput.addEventListener('blur', () => {
        qtyInput.value = roundUp(parseInt(qtyInput.value, 10) || min);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        paintQty();
      });
    }

    /* ---------------- Sticky bar mirror ---------------- */
    /* The bar is a MIRROR, never an independent control: it has no stepper,
       so it must state the quantity it will add, and its label and colour
       always match the panel's state. */
    const bar = document.querySelector('[data-wb-buybar]');
    const barPrice = bar && bar.querySelector('[data-wb-bar-price]');
    const barText = bar && bar.querySelector('[data-wb-bar-cta-text]');
    const barCta = bar && bar.querySelector('[data-wb-bar-cta]');
    /* Ticked PDP add-ons (assets/wb-addons.js). The add-on block sits BELOW
       the panel CTA, so by the time a customer is ticking things this bar is
       the button they will actually press — it has to state what that press
       will cost and add. wb-addons.js announces its units and cents on every
       change; syncBar folds them in. One writer for the bar, still this file. */
    let addons = { units: 0, cents: 0 };
    document.addEventListener('wb:addons:change', (e) => {
      const d = (e && e.detail) || {};
      addons = { units: d.units | 0, cents: d.cents | 0 };
      syncBar();
    });
    const priceEl = root.querySelector('[data-wb-price]');
    const state = root.dataset.state;
    const unitCents = priceEl ? parseInt(priceEl.dataset.unitCents, 10) || 0 : 0;
    /* ⚠ Use the THEME's formatter with the shop's real money_format. The
       hand-rolled version this replaces hard-coded "$" in BOTH branches of its
       ternary and a comma/period separator, so every non-AUD market rendered
       "$204,60" instead of "€204,60". FoxTheme.Currency handles all of
       Shopify's amount_* formats. Returns null if the theme bundle is absent,
       and every caller then leaves the server-rendered text alone rather than
       print a number in the wrong currency. */
    const moneyFormat = priceEl ? priceEl.dataset.moneyFormat : null;
    const money = (cents) => {
      const C = window.FoxTheme && window.FoxTheme.Currency;
      return C && C.formatMoney ? C.formatMoney(cents, moneyFormat) : null;
    };

    /* Line total for products sold by the metre — 30 m at $6.82 is arithmetic
       the buyer should not have to do. Recomputed on every quantity change and
       reads unit-cents fresh, which wb-variants.js keeps current. */
    const lineTotal = root.querySelector('[data-wb-line-total]');
    function paintLineTotal() {
      if (!lineTotal) return;
      const q = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
      const cents = priceEl ? parseInt(priceEl.dataset.unitCents, 10) || 0 : 0;
      const total = q > 0 ? money(cents * q) : null;
      if (total === null) return;
      /* Feet alongside metres so an imperial buyer can dial in a spec — step
         to 61 and it reads "61 m (200 ft)". Metres stay the ONLY unit of sale;
         see the note on the stepper for why there is no ft input.
         Built as NODES, not innerHTML: the feet phrase needs its own element
         to sit back visually, and the surrounding text is a translated string
         that must never be parsed as markup. Split on the placeholder so a
         locale is free to order the conversion differently. */
      const tmpl = lineTotal.dataset.tLine || '';
      const ftTmpl = lineTotal.dataset.tFt || '';
      const fill = (str) => str.replace('[qty]', q).replace('[total]', total);
      const around = tmpl.split('[ft]');
      const ftSpan = document.createElement('span');
      ftSpan.className = 'wb-line-total__ft';
      ftSpan.textContent = ftTmpl.replace('[ft]', Math.round(q * 3.280839895));
      lineTotal.textContent = fill(around[0] || '');
      lineTotal.appendChild(ftSpan);
      if (around[1]) lineTotal.appendChild(document.createTextNode(fill(around[1])));
    }
    function syncBar() {
      if (!bar || !barText) return;
      /* ⚠ Reads the panel CTA's LIVE disabled flag, not the server-rendered
         `state`, which is frozen at page load. After a variant switch to a
         sold-out option the stale value let the next quantity change relabel
         the bar "ADD TO CART" over a disabled panel button. */
      if (cta && cta.disabled) {
        if (barPrice) { const m = money(unitCents); if (m !== null) barPrice.textContent = m; }
        barText.textContent = cta.textContent.trim() || barText.textContent;
        return;
      }
      if (state === 'oos' || state === 'below-moq') {
        // A line total for an unbuyable quantity would be nonsense.
        if (barPrice) { const m = money(unitCents); if (m !== null) barPrice.textContent = m; }
        barText.textContent = (cta && cta.textContent.trim()) || barText.textContent;
        return;
      }
      const q = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (addons.units > 0) {
        /* Price is the whole add — parent line plus every ticked add-on —
           and the label mirrors the panel CTA, which wb-addons.js has already
           set to its "ADD N ITEMS TO CART" form. Mirroring rather than
           re-deriving keeps the two buttons saying the same words. */
        if (barPrice) { const m = money(unitCents * q + addons.cents); if (m !== null) barPrice.textContent = m; }
        barText.textContent = (cta && cta.textContent.trim()) || barText.textContent;
        return;
      }
      if (barPrice) { const m = money(unitCents * q); if (m !== null) barPrice.textContent = m; }
      /* ⚠ No English literals here — see the note on the bar's markup. If an
         attribute is somehow missing we keep whatever the server rendered
         rather than substitute a guess in the wrong language. */
      const d = bar.dataset;
      if (state === 'backorder') {
        if (d.tCtaBackorder) barText.textContent = d.tCtaBackorder;
      } else if (q > 1) {
        if (d.tCtaQty) barText.textContent = d.tCtaQty.replace('[count]', q);
      } else if (d.tCta) {
        barText.textContent = d.tCta;
      }
    }
    if (barCta && cta) barCta.addEventListener('click', () => cta.click());

    /* ---------------- Scroll-driven chrome ---------------- */
    /* rAF-throttled scroll read, NOT an IntersectionObserver: an IO only
       fires when the element CROSSES a threshold, so an instant jump from
       off-screen-above to off-screen-below never fires and the bar keeps a
       stale state (restored scroll on reload, back/forward, anchor jumps). */
    const footer = document.querySelector('.footer, #shopify-section-footer, footer');
    let shownBar = null, shownTop = null, ticking = false;
    const apply = () => {
      ticking = false;
      if (cta && bar) {
        // Strictly directional: the CTA has left through the TOP. "Not
        // visible" would show the bar before any scroll, since the CTA
        // starts below the fold.
        const pastCta = cta.getBoundingClientRect().bottom <= 0;
        const footerInView = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const show = pastCta && !footerInView;
        if (show !== shownBar) {
          shownBar = show;
          document.body.classList.toggle('wb-show-buybar', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        }
      }
      const top = window.scrollY > window.innerHeight;
      if (top !== shownTop) {
        shownTop = top;
        document.body.classList.toggle('wb-show-backtop', top);
      }
    };
    const schedule = () => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } };
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule, { passive: true });

    /* ---------------- Dispatch countdown ---------------- */
    /* Server-rendered timestamp + client tick, computed in the SHOP's
       timezone — never the browser clock, or an overseas shopper sees a
       wrong number. */
    const dispatch = root.querySelector('[data-wb-dispatch]');
    if (dispatch) {
      const textEl = dispatch.querySelector('[data-wb-dispatch-text]');
      const d = dispatch.dataset;
      const cutoffHour = parseInt(dispatch.dataset.cutoffHour, 10) || 15;
      const serverNow = parseInt(dispatch.dataset.now, 10) * 1000;
      const offsetStr = dispatch.dataset.shopOffset || '+0000';
      const offsetMin = (parseInt(offsetStr.slice(0, 3), 10) * 60) + (parseInt(offsetStr[0] + offsetStr.slice(3), 10) || 0);
      const drift = serverNow - Date.now();
      /* ⚠ Business days come from WBHolidays when it is loaded — the same
         calendar the delivery estimator uses, so the countdown can never
         say "Dispatches today" on a day the estimator knows is the Gold
         Coast Show. Weekday-only logic is the no-JS-calendar fallback. */
      /* Which warehouse ships this product — decided in Liquid from
         custom.dsi_type, never re-derived here. Drives both the calendar
         (SA vs QLD public holidays, plus any closure) and the timezone. */
      const originCode = d.origin || 'main';
      const isBiz = (dt) => (window.WBHolidays
        ? window.WBHolidays.isBusinessDay(dt, originCode)
        : (dt.getUTCDay() >= 1 && dt.getUTCDay() <= 5));
      /* Copy comes from the locale files via data-t-* (see the note in
         sections/wb-main-product.liquid). Fallbacks keep the row sane if the
         attributes are ever missing. */
      const T = {
        today: d.tToday || 'Dispatches today',
        within: d.tWithin || 'order within [left]',
        on: d.tOn || 'Dispatches [day]',
        tomorrow: d.tTomorrow || 'tomorrow',
        cutoff: d.tCutoff || 'Cut-off is [time] your time, business days',
        hr: d.tHr || 'hr', hrs: d.tHrs || 'hrs', mins: d.tMins || 'mins'
      };

      const docLang = document.documentElement.lang || 'en';
      const locale = docLang === 'en' ? 'en-AU' : docLang;

      /* ⚠ The cutoff is shown on the SHOPPER'S clock, deliberately — Owen does
         not want the warehouse's city named, and a zone label cannot serve
         Perth, Darwin and a Sydney summer at once ("3pm AEST" is 4pm on a
         Sydney clock from October to April). So the cutoff has to become a real
         UTC INSTANT first, then be formatted in whatever zone the browser is.

         ⚠ Going from "midday in Adelaide on the 4th" back to an instant cannot
         use a fixed offset — that is the whole reason this file stopped using
         one. The offset is measured AT that date, then re-measured once at the
         resulting instant: the first measurement can land on the wrong side of
         a daylight-saving change, and the second lands it correctly. */
      const zoneOffsetMinAt = (instant) => {
        if (!zone || typeof Intl === 'undefined') return offsetMin;
        try {
          const p = new Intl.DateTimeFormat('en-AU', {
            timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
          }).formatToParts(instant).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
          const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute);
          return Math.round((asUTC - instant.getTime()) / 60000);
        } catch (e) { return offsetMin; }
      };
      const instantForLocal = (y, m, dd, hour) => {
        const pretend = Date.UTC(y, m - 1, dd, hour, 0, 0, 0);
        let off = zoneOffsetMinAt(new Date(pretend));
        off = zoneOffsetMinAt(new Date(pretend - off * 60000));
        return new Date(pretend - off * 60000);
      };
      const cutInstant = (dayUTC) =>
        instantForLocal(dayUTC.getUTCFullYear(), dayUTC.getUTCMonth() + 1, dayUTC.getUTCDate(), cutoffHour);
      const fmtTime = (inst) => inst.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
      /* Shifted-space dates are midnight UTC, so they must be READ back as UTC
         or a westward shopper sees the previous day. */
      const fmtDay = (dayShifted) =>
        dayShifted.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

      /* ⚠ Warehouse wall-clock, from an IANA ZONE rather than a fixed offset.
         Arundel is UTC+10 all year, but EDMO is Adelaide: +9:30 in winter and
         +10:30 under daylight saving. A numeric offset would be right for half
         the year and an hour out for the other half — and an hour is the whole
         decision at a cutoff. Intl does the DST lookup per instant.

         Falls back to the server-rendered offset only where Intl is missing,
         which no browser this theme supports actually is. */
      const zone = d.originTz || '';
      const wallClock = (instant) => {
        if (zone && typeof Intl !== 'undefined') {
          try {
            const p = new Intl.DateTimeFormat('en-AU', {
              timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric',
              hour: 'numeric', hourCycle: 'h23'
            }).formatToParts(instant).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
            /* h23 plus a %24 belt: some engines emit "24" for midnight, which
               reads as after any cutoff and rolls dispatch a day late. */
            return { y: +p.year, m: +p.month, d: +p.day, hr: (+p.hour) % 24 };
          } catch (e) { /* fall through */ }
        }
        const shifted = new Date(instant.getTime() + offsetMin * 60000);
        return {
          y: shifted.getUTCFullYear(), m: shifted.getUTCMonth() + 1,
          d: shifted.getUTCDate(), hr: shifted.getUTCHours()
        };
      };

      const tick = () => {
        const instant = new Date(Date.now() + drift);
        const w = wallClock(instant);
        const today = new Date(Date.UTC(w.y, w.m - 1, w.d));
        const hr = w.hr;
        if (isBiz(today) && hr < cutoffHour) {
          const cut = cutInstant(today);
          const mins = Math.max(0, Math.round((cut - instant) / 60000));
          const h = Math.floor(mins / 60), m = mins % 60;
          const left = h > 0 ? h + ' ' + (h > 1 ? T.hrs : T.hr) + ' ' + m + ' ' + T.mins : m + ' ' + T.mins;
          textEl.innerHTML = '<strong>' + T.today + '</strong> — ' + T.within.replace('[left]', left);
        } else {
          const next = new Date(today.getTime());
          /* ⚠ Bounded. This loop is the only thing standing between a bad
             `data-now` / `data-shop-offset` and a hung tab: without WBHolidays
             loaded, isBiz() on an Invalid Date is false forever. 14 days is
             past any real closure, and falling out early degrades to a
             slightly-off label rather than a frozen page. */
          let guard = 0;
          do { next.setUTCDate(next.getUTCDate() + 1); } while (!isBiz(next) && ++guard < 14);
          const diff = Math.round((next - today) / 86400000);
          /* An absolute date beyond tomorrow: a bare weekday five days out
             ("Dispatches Tuesday" on the Thursday before Easter) reads as this
             week. Same format the estimator prints in the row below, so the
             two dates in one card cannot look like different things. */
          const label = diff === 1 ? T.tomorrow : fmtDay(next);
          /* The cutoff that matters is the one on the day it will actually
             dispatch, not today's — across a DST change in the shopper's own
             zone those are different local times. */
          textEl.innerHTML = '<strong>' + T.on.replace('[day]', label) + '</strong>. ' +
            T.cutoff.replace('[time]', fmtTime(cutInstant(next)));
        }
      };
      tick();
      setInterval(tick, 30000);
    }

    /* ---- The rating badge jumps to the reviews ----
       Judge.me renders the preview badge as plain divs with no link of its
       own, and its click handler looks for a widget id this template does not
       use — so the stars and "8 reviews" were dead (Owen, 2026-08-09).

       Only the badge becomes interactive, never the whole row: the SKU sits
       beside it and must stay selectable text. role/tabindex rather than an
       <a> wrapper because Judge.me may inject its own anchor inside the badge
       once its script runs, and nested links are invalid. */
    const ratingRow = document.querySelector('.wb-rating-row');
    const reviewsSec = document.getElementById('reviews');
    if (ratingRow && reviewsSec) {
      const badge = ratingRow.querySelector('.jdgm-widget, .jdgm-prev-badge');
      if (badge && !badge.classList.contains('wb-rating-jump')) {
        badge.classList.add('wb-rating-jump');
        badge.setAttribute('role', 'link');
        badge.setAttribute('tabindex', '0');
        const jump = (e) => {
          e.preventDefault();
          // Judge.me binds its own listener to the same element; without this
          // it also fires and fights us for the scroll position.
          e.stopPropagation();
          // scrollIntoView honours scroll-margin-top, which is what keeps the
          // heading clear of the pinned header + tab bar (--anchor-offset).
          reviewsSec.scrollIntoView({ block: 'start' });
          // Match the tab links, which are plain #fragment anchors, without
          // adding a second history entry for what is a scroll.
          if (history.replaceState) history.replaceState(null, '', '#reviews');
        };
        badge.addEventListener('click', jump);
        badge.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') jump(e);
        });
      }
    }

    paintQty();
    syncBar();
    paintLineTotal();
    apply();
    skuCopy();
  };

  /* Click-to-copy on the SKU.

     Delegated from document, not bound to the button, because wb-variants.js
     rewrites this region on variant change. A bound listener would survive on a
     detached node and go quiet.

     The SKU text is read at click time and the label stripped off the front, so
     the shopper gets "CAV110" rather than "SKU: CAV110". */
  var skuCopy = function () {
    var TIMEOUT = 1600;
    var timer = null;

    var flash = function (btn, msg, failed) {
      var bubble = btn.querySelector('[data-wb-sku-bubble]');
      if (!bubble) return;
      clearTimeout(timer);
      bubble.textContent = msg;
      btn.classList.toggle('is-failed', !!failed);
      btn.classList.add('is-copied');
      timer = setTimeout(function () {
        btn.classList.remove('is-copied', 'is-failed');
        /* cleared so the live region does not re-announce stale text, and so an
           empty bubble never renders if a stylesheet fails to load */
        bubble.textContent = '';
      }, TIMEOUT);
    };

    /* execCommand path for browsers that reject the async clipboard API —
       Safari refuses it outside a user gesture in some configurations, and it
       is unavailable entirely on http. */
    var legacyCopy = function (text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
      document.body.appendChild(ta);
      var sel = document.getSelection();
      var prev = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      /* restore whatever the shopper had selected before we hijacked it */
      if (prev && sel) { sel.removeAllRanges(); sel.addRange(prev); }
      return ok;
    };

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-wb-sku-copy]');
      if (!btn) return;
      var el = btn.querySelector('.wb-sku');
      if (!el || el.hidden) return;

      var raw = (el.textContent || '').trim();
      var label = el.dataset.wbSkuLabel || '';
      /* strip "SKU: " however the locale punctuates it — the label is whatever
         the translation produced, and some locales use a full-width colon */
      var value = raw;
      if (label && raw.indexOf(label) === 0) value = raw.slice(label.length);
      value = value.replace(/^\s*[:：]\s*/, '').trim();
      if (!value) return;

      var done = function () { flash(btn, btn.dataset.wbCopied || 'Copied', false); };
      var fail = function () { flash(btn, btn.dataset.wbCopyFailed || 'Press Ctrl+C', true); };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done, function () {
          legacyCopy(value) ? done() : fail();
        });
      } else {
        legacyCopy(value) ? done() : fail();
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
